/**
 * purchase-options.js
 *
 * NOVOLD Phase B — 購入オプションUI（単発 vs 定期 / 配達ペース）の web component。
 *
 * 責務:
 *  1. ラジオ選択に応じて buy-buttons の <form> に hidden input[name="selling_plan"] を inject/update
 *  2. vs-frequency fieldset の hidden / disabled 切替
 *  3. chip の .active class 同期
 *  4. chip 内 .mini の価格表示更新（容量切替対応）
 *  5. 上部メイン価格 <product-price data-product-id="..."> の .price 要素を allocation 価格に書き換え
 *
 * 触らないもの:
 *  - <product-price> 内の .compare-at-price（Phase B 方針）
 *  - 既存の variant-picker / buy-buttons / price block の Liquid 出力構造
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

class PurchaseOptionsComponent extends Component {
  /** @type {Record<string, any>} */
  prices = {};

  /** @type {NodeListOf<HTMLInputElement> | null} */
  purchaseRadios = null;

  /** @type {NodeListOf<HTMLInputElement> | null} */
  frequencyRadios = null;

  /** @type {HTMLFieldSetElement | null} */
  frequencyFieldset = null;

  /** @type {string} */
  sectionId = '';

  /** @type {string} */
  productId = '';

  connectedCallback() {
    super.connectedCallback();

    this.sectionId = this.dataset.sectionId || '';
    this.productId = this.dataset.productId || '';

    const priceScript = this.querySelector('[data-purchase-options-prices]');
    if (priceScript && priceScript.textContent) {
      try {
        this.prices = JSON.parse(priceScript.textContent);
      } catch (err) {
        console.error('[purchase-options] failed to parse prices JSON', err);
        this.prices = {};
      }
    }

    this.purchaseRadios = this.querySelectorAll(
      `input[name="purchase_option_${this.sectionId}"]`
    );
    this.frequencyRadios = this.querySelectorAll(
      `input[name="frequency_${this.sectionId}"]`
    );
    this.frequencyFieldset = this.querySelector('[data-frequency-section]');

    this.purchaseRadios.forEach((radio) => {
      radio.addEventListener('change', this.handleChange);
    });
    this.frequencyRadios.forEach((radio) => {
      radio.addEventListener('change', this.handleChange);
    });

    document.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);

    // 初回同期
    this.sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    if (this.purchaseRadios) {
      this.purchaseRadios.forEach((r) => r.removeEventListener('change', this.handleChange));
    }
    if (this.frequencyRadios) {
      this.frequencyRadios.forEach((r) => r.removeEventListener('change', this.handleChange));
    }
  }

  handleChange = () => {
    this.sync();
  };

  /**
   * variant 切替（容量200g→500g 等）後は Ritual が <product-price> 内を再レンダする。
   * 再レンダ後の DOM では dataset.purchaseOptionsOriginal が消えるため、
   * 既存値もリセットして sync を queueMicrotask 越しに再実行する。
   */
  handleVariantUpdate = (event) => {
    // 自分の商品の variant 更新だけを対象にする
    const eventProductId = event?.detail?.data?.productId;
    if (eventProductId && this.productId && String(eventProductId) !== String(this.productId)) {
      return;
    }

    queueMicrotask(() => {
      // Ritual の再レンダで dataset がリセットされるため、念のため明示的に削除
      const productPriceEls = document.querySelectorAll(
        `product-price[data-product-id="${this.productId}"]`
      );
      productPriceEls.forEach((el) => {
        const targets = el.querySelectorAll(
          '.price__regular > .price, .price__sale .price-item--sale.price'
        );
        targets.forEach((t) => {
          delete t.dataset.purchaseOptionsOriginal;
        });
      });
      this.sync();
    });
  };

  sync() {
    const purchase = this.getPurchaseValue();
    const sellingPlanId =
      purchase === 'subscription' ? this.getActiveSellingPlanId() : '';

    // 1) buy-buttons の form に hidden input を inject/update
    this.injectSellingPlanInput(sellingPlanId);

    // 2) frequency fieldset の表示制御
    if (this.frequencyFieldset) {
      const isSubscription = purchase === 'subscription';
      this.frequencyFieldset.toggleAttribute('hidden', !isSubscription);
      if (isSubscription) {
        this.frequencyFieldset.removeAttribute('aria-disabled');
      } else {
        this.frequencyFieldset.setAttribute('aria-disabled', 'true');
      }
      if (this.frequencyRadios) {
        this.frequencyRadios.forEach((r) => {
          r.disabled = !isSubscription;
          if (isSubscription) {
            r.removeAttribute('aria-disabled');
          } else {
            r.setAttribute('aria-disabled', 'true');
          }
        });
      }
    }

    // 2.5) 定期購入時のみ perks（送料無料/スキップ自由/いつでも解約OK）を表示
    const perksEl = this.querySelector('[data-subscription-perks]');
    if (perksEl) perksEl.toggleAttribute('hidden', purchase !== 'subscription');

    // 3) chip の active class 同期
    if (this.purchaseRadios) {
      this.purchaseRadios.forEach((r) => {
        const chip = r.closest('.vs-chip');
        if (chip) chip.classList.toggle('active', r.checked);
      });
    }
    if (this.frequencyRadios) {
      this.frequencyRadios.forEach((r) => {
        const chip = r.closest('.vs-chip');
        if (chip) chip.classList.toggle('active', r.checked);
      });
    }

    // 4) chip 内 .mini 価格表示の更新（active variant + 選択肢に応じて）
    this.updateChipPrices();

    // 4.5) 「定期で¥XXXおトク」save tag を active variant に合わせて更新
    this.updateSaveTag();

    // 5) 上部メイン価格 <product-price> の .price 要素を allocation.price で書き換え
    this.updateMainPrice(purchase);
  }

  /**
   * 定期割引額 save tag を active variant + 2week plan に合わせて更新。
   * onetime - subscription_2w の差額を「¥XXX」形式で出力。
   * 該当 plan が無い variant は tag を hidden化。
   */
  updateSaveTag() {
    const saveTag = this.querySelector('[data-purchase-save-tag]');
    const saveAmount = this.querySelector('[data-purchase-save-amount]');
    if (!saveTag || !saveAmount) return;

    const variantId = this.getCurrentVariantId();
    const set = variantId ? this.prices[String(variantId)] : null;
    if (!set || set.subscription_month == null || set.onetime == null) {
      saveTag.setAttribute('hidden', '');
      return;
    }
    // Shopify は variant.price/alloc.price を「最小通貨単位 × 100」で返す
    // JPY store でも 7150円は 715000 で出力されるので /100 して円単位化
    const diffCent = Number(set.onetime) - Number(set.subscription_month);
    if (!Number.isFinite(diffCent) || diffCent <= 0) {
      saveTag.setAttribute('hidden', '');
      return;
    }
    const diffYen = Math.round(diffCent / 100);
    saveTag.removeAttribute('hidden');
    saveAmount.textContent = '¥' + diffYen.toLocaleString('ja-JP');
  }

  getPurchaseValue() {
    if (!this.purchaseRadios) return null;
    const checked = Array.from(this.purchaseRadios).find((r) => r.checked);
    return checked ? checked.value : null;
  }

  getActiveSellingPlanId() {
    // 選択中の variant に対応する selling_plan id を引く。
    // ラジオの data-selling-plan-id は first_group（例: 200g）固定なので、
    // 500g 等サイズ違い variant では不一致になり /cart/add が 422 で落ちる。
    // prices[variantId] には variant ごとの正しい plan id が入っているのでそれを最優先で使う。
    const freq = this.getActiveFrequencyValue(); // '2week' | 'month'
    const variantId = this.getCurrentVariantId();
    const set = variantId ? this.prices[String(variantId)] : null;
    if (set) {
      const planId =
        freq === 'month' ? set.subscription_month_plan_id : set.subscription_2w_plan_id;
      // variant に対応プランが無ければ空文字（= 単発として追加し、422 を避ける）
      return planId ? String(planId) : '';
    }
    // prices マップが無い場合のみ、従来のラジオ dataset にフォールバック
    if (!this.frequencyRadios) return '';
    const checked = Array.from(this.frequencyRadios).find((r) => r.checked);
    return checked?.dataset.sellingPlanId || '';
  }

  /**
   * 同一 section の buy-buttons form (#BuyButtons-ProductForm-<sectionId>) に
   * hidden input[name="selling_plan"] を inject/update する。
   */
  injectSellingPlanInput(sellingPlanId) {
    if (!this.sectionId) return;
    const form = document.querySelector(`#BuyButtons-ProductForm-${this.sectionId}`);
    if (!form) return;
    let hidden = form.querySelector('input[name="selling_plan"][data-purchase-options-target]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'selling_plan';
      hidden.setAttribute('data-purchase-options-target', '');
      form.appendChild(hidden);
    }
    hidden.value = sellingPlanId || '';
  }

  /**
   * variant id を form の input[name="id"] から取得（Ritual の標準実装）。
   */
  getCurrentVariantId() {
    if (!this.sectionId) return null;
    const form = document.querySelector(`#BuyButtons-ProductForm-${this.sectionId}`);
    const idInput = form?.querySelector('input[name="id"]');
    return idInput?.value || null;
  }

  /**
   * chip 内 .mini 価格テキストを active variant + plan に合わせて更新。
   * - data-purchase-mini="onetime" は variant.title + onetime_formatted
   * - data-purchase-mini="subscription" は variant.title + subscription_2w_formatted（強調）+ 実額差分ピル（差額が無ければ非表示）
   */
  updateChipPrices() {
    const variantId = this.getCurrentVariantId();
    if (!variantId) return;
    const set = this.prices[String(variantId)];
    if (!set) return;

    const purchase = this.getPurchaseValue();
    const freq = this.getActiveFrequencyValue();

    // onetime mini
    const onetimeMini = this.querySelector('[data-purchase-mini="onetime"]');
    if (onetimeMini && set.onetime_formatted) {
      onetimeMini.innerHTML = `${this.escapeHtml(set.title || '')} ${set.onetime_formatted}`;
    }

    // subscription mini（freq に応じて 2w / month を出し分け）
    // CSS が override で勝てない問題を回避するため、毎回 innerHTML 全rebuildで
    // <em> を inline style付きで強制再生成
    const subMini = this.querySelector('[data-purchase-mini="subscription"]');
    if (subMini) {
      // getActiveFrequencyValue() が 'month' 固定のため、実質 subscription_month_formatted のみが使われる
      const subFormatted =
        freq === 'month' ? set.subscription_month_formatted : set.subscription_2w_formatted;
      const titleSafe = this.escapeHtml(set.title || '');
      const priceSafe = subFormatted || '';
      const pillStyle = 'background-color:#ffffff !important;background-image:none !important;background:#ffffff !important;color:#0a0a0a !important;padding:2px 7px;border-radius:3px;font-style:normal;font-weight:800;font-size:11px;display:inline-block;line-height:1.4;margin-left:5px;';
      // Shopify は variant.price/alloc.price を「最小通貨単位 × 100」で返す（updateSaveTag() と同様に /100 して円単位化）
      const subRaw = freq === 'month' ? set.subscription_month : set.subscription_2w;
      let pillHtml = '';
      if (subRaw != null && set.onetime != null) {
        const diffCent = Number(set.onetime) - Number(subRaw);
        if (Number.isFinite(diffCent) && diffCent > 0) {
          const diffYen = Math.round(diffCent / 100);
          pillHtml = ` <span class="vs-discount-pill" style="${pillStyle}">-¥${diffYen.toLocaleString('ja-JP')}</span>`;
        }
      }
      subMini.innerHTML =
        `${titleSafe} <strong data-purchase-mini-strong>${priceSafe}</strong>` + pillHtml;
    }

    // 未使用変数を抑制（lint warning 回避）
    void purchase;
  }

  getActiveFrequencyValue() {
    // ペース選択UI廃止につき1か月固定
    return 'month';
  }

  /**
   * 上部メイン価格（Ritual auto-generated <product-price>）の .price 要素を
   * 単発 / 定期 / 周期 に応じて書き換え。
   * - subscription 選択時: allocation の money 済み文字列に差し替え
   * - onetime 選択時: 元の Liquid 出力に戻す（dataset.purchaseOptionsOriginal に保存）
   *
   * 注意: data-product-id セレクタで全 product-price を取り、同一商品を表示している
   * 全箇所（sticky bar 含む）を一括同期する。
   * compare-at-price は Phase B では触らない。
   */
  updateMainPrice(purchase) {
    if (!this.productId) return;

    const variantId = this.getCurrentVariantId();
    const set = variantId ? this.prices[String(variantId)] : null;

    let formatted = null;
    if (purchase === 'subscription' && set) {
      const freq = this.getActiveFrequencyValue();
      formatted =
        freq === 'month' ? set.subscription_month_formatted : set.subscription_2w_formatted;
    }
    // onetime のときは formatted = null → 元テキスト復元

    const productPriceEls = document.querySelectorAll(
      `product-price[data-product-id="${this.productId}"]`
    );

    if (productPriceEls.length === 0) {
      console.warn('[purchase-options] no <product-price> found for productId=', this.productId);
      return;
    }

    productPriceEls.forEach((el) => {
      const targets = el.querySelectorAll(
        '.price__regular > .price, .price__sale .price-item--sale.price'
      );
      if (targets.length === 0) {
        console.warn('[purchase-options] price targets not found inside <product-price>');
        return;
      }
      targets.forEach((t) => {
        if (!t.dataset.purchaseOptionsOriginal) {
          t.dataset.purchaseOptionsOriginal = t.innerHTML;
        }
        if (formatted) {
          t.innerHTML = formatted;
        } else {
          t.innerHTML = t.dataset.purchaseOptionsOriginal;
        }
      });
    });
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('purchase-options-component')) {
  customElements.define('purchase-options-component', PurchaseOptionsComponent);
}
